import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuidV4 } from "uuid";
import { firestore, storage, FieldValue } from "../../config/firebaseConfig";
import { saveRebuildRecordAsync } from "./rebuildLogs";

// Bulk intake for the handwritten paper reports. A scanned PDF lands in
// Storage at `rebuild/scans/{batchId}/source.pdf`; the processRebuildScan
// function splits it, reads each page against the live form catalog, and
// stages PENDING records here. Nothing reaches `rebuildLogs` until someone
// approves it below — traceability data never auto-files.

const BATCH_LIMIT = 25;

let batchesUnsub = null;
let recordsUnsub = null;

export const fetchScanBatchesAsync = createAsyncThunk(
  "rebuildScans/fetchBatches",
  async (_, thunkAPI) => {
    if (batchesUnsub) batchesUnsub();
    thunkAPI.dispatch(setLoadingBatches(true));
    batchesUnsub = firestore
      .collection("rebuildScanBatches")
      .orderBy("uploadedAt", "desc")
      .limit(BATCH_LIMIT)
      .onSnapshot(
        (snapshot) => {
          const items = {};
          snapshot.forEach((doc) => {
            items[doc.id] = { id: doc.id, ...doc.data() };
          });
          thunkAPI.dispatch(setBatches(items));
        },
        (error) => {
          console.error("rebuildScanBatches listener error", error);
          thunkAPI.dispatch(setError(error.message));
        }
      );
  }
);

export const cancelFetchScanBatches = createAsyncThunk(
  "rebuildScans/cancelBatches",
  async () => {
    if (batchesUnsub) {
      batchesUnsub();
      batchesUnsub = null;
    }
  }
);

export const fetchScanRecordsAsync = createAsyncThunk(
  "rebuildScans/fetchRecords",
  async (batchId, thunkAPI) => {
    if (recordsUnsub) recordsUnsub();
    thunkAPI.dispatch(setLoadingRecords(true));
    recordsUnsub = firestore
      .collection("rebuildScanBatches")
      .doc(batchId)
      .collection("records")
      .onSnapshot(
        (snapshot) => {
          const items = [];
          snapshot.forEach((doc) => items.push({ id: doc.id, ...doc.data() }));
          items.sort((a, b) =>
            a.pageNumber === b.pageNumber
              ? (a.blockIndex || 0) - (b.blockIndex || 0)
              : a.pageNumber - b.pageNumber
          );
          thunkAPI.dispatch(setRecords(items));
        },
        (error) => {
          console.error("scan records listener error", error);
          thunkAPI.dispatch(setError(error.message));
        }
      );
  }
);

export const cancelFetchScanRecords = createAsyncThunk(
  "rebuildScans/cancelRecords",
  async () => {
    if (recordsUnsub) {
      recordsUnsub();
      recordsUnsub = null;
    }
  }
);

// Create the batch doc first so the review page can show it immediately, then
// upload — the Storage trigger fires on the finished upload.
export const uploadScanBatchAsync = createAsyncThunk(
  "rebuildScans/upload",
  async ({ file, location, user }, thunkAPI) => {
    try {
      const batchId = uuidV4();
      const ref = firestore.collection("rebuildScanBatches").doc(batchId);

      // The batch doc must carry the shop before the upload finishes: the
      // Storage trigger fires on the upload and reads it from here.
      await ref.set({
        fileName: file.name,
        location: String(location || "").toLowerCase(),
        status: "uploading",
        error: null,
        uploadedAt: FieldValue.serverTimestamp(),
        uploadedBy: { uid: user?.uid || "", name: user?.name || "" },
      });

      const filePath = `rebuild/scans/${batchId}/source.pdf`;
      const snap = await storage.ref(filePath).put(file);
      const downloadUrl = await snap.ref.getDownloadURL();

      await ref.set(
        { status: "uploaded", sourcePath: filePath, sourceUrl: downloadUrl },
        { merge: true }
      );

      return { batchId };
    } catch (error) {
      console.error("uploadScanBatch failed", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Reviewer edits stay on the staged record until it is approved.
export const updateScanRecordAsync = createAsyncThunk(
  "rebuildScans/updateRecord",
  async ({ batchId, recordId, values }, thunkAPI) => {
    try {
      await firestore
        .collection("rebuildScanBatches")
        .doc(batchId)
        .collection("records")
        .doc(recordId)
        .set(
          { values, updatedAt: FieldValue.serverTimestamp() },
          { merge: true }
        );
      return { recordId };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const rejectScanRecordAsync = createAsyncThunk(
  "rebuildScans/rejectRecord",
  async ({ batchId, recordId, undo }, thunkAPI) => {
    try {
      await firestore
        .collection("rebuildScanBatches")
        .doc(batchId)
        .collection("records")
        .doc(recordId)
        .set(
          {
            status: undo ? "pending" : "rejected",
            updatedAt: FieldValue.serverTimestamp(),
          },
          { merge: true }
        );
      return { recordId };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Turn a staged record into a real rebuild log entry. The scanned page rides
// along as the record's attachment so the paper original stays reachable.
const toRebuildPayload = (record, user) => {
  const v = record.values || {};
  const isManifold = record.action === "manifoldRebuild";

  return {
    assetNumber: isManifold ? v.skidNumber || "" : v.assetNumber || "",
    asset: isManifold ? v.skidNumber || "" : v.assetNumber || "",
    skidNumber: v.skidNumber || null,
    components: isManifold ? v.components || [] : [],
    oem: v.oem || null,
    description: v.description || "",
    date: v.date || "",
    tech: v.tech || "",
    location: (v.location || "").toLowerCase(),
    outcome: v.outcome || null,
    notes: v.notes || "",
    bomEntries: v.bomEntries || [],
    // Attestations and other template fields the reviewer filled in while
    // looking at the page — carried through to the filed record.
    customFieldValues: v.customFieldValues || {},
    files: [
      {
        filePath: record.pageFilePath,
        downloadUrl: record.pageDownloadUrl,
        fileName: `Scanned report p${record.pageNumber}.pdf`,
      },
    ],
    action: record.action || "rebuild",
    formId: record.formId || null,
    formName: record.formName || null,
    category: record.category || null,
    docNumber: record.docNumber || null,
    docRev: record.docRev || null,
    completedAt: null,
    completedBy: null,
    updatedBy: user?.uid || null,
    // Provenance: which scan batch and page this came from.
    source: "scan",
    scanBatchId: record.batchId,
    scanRecordId: record.id,
    scanPage: record.pageNumber,
  };
};

export const approveScanRecordAsync = createAsyncThunk(
  "rebuildScans/approveRecord",
  async ({ batchId, record, user }, thunkAPI) => {
    try {
      const payload = toRebuildPayload(record, user);
      const saved = await thunkAPI
        .dispatch(saveRebuildRecordAsync({ values: payload, user }))
        .unwrap();

      await firestore
        .collection("rebuildScanBatches")
        .doc(batchId)
        .collection("records")
        .doc(record.id)
        .set(
          {
            status: "approved",
            committedRecordId: saved.id,
            approvedAt: FieldValue.serverTimestamp(),
            approvedBy: { uid: user?.uid || "", name: user?.name || "" },
          },
          { merge: true }
        );

      return { recordId: record.id, committedRecordId: saved.id };
    } catch (error) {
      console.error("approveScanRecord failed", error);
      return thunkAPI.rejectWithValue(
        typeof error === "string" ? error : error.message
      );
    }
  }
);

const rebuildScansSlice = createSlice({
  name: "rebuildScans",
  initialState: {
    batches: {},
    records: [],
    loadingBatches: false,
    loadingRecords: false,
    uploading: false,
    working: null,
    error: null,
  },
  reducers: {
    setBatches(state, action) {
      state.batches = action.payload;
      state.loadingBatches = false;
      state.error = null;
    },
    setRecords(state, action) {
      state.records = action.payload;
      state.loadingRecords = false;
    },
    setLoadingBatches(state, action) {
      state.loadingBatches = action.payload;
    },
    setLoadingRecords(state, action) {
      state.loadingRecords = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loadingBatches = false;
      state.loadingRecords = false;
    },
    clearScanRecords(state) {
      state.records = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadScanBatchAsync.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadScanBatchAsync.fulfilled, (state) => {
        state.uploading = false;
      })
      .addCase(uploadScanBatchAsync.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload;
      })
      .addCase(approveScanRecordAsync.pending, (state, action) => {
        state.working = action.meta.arg.record.id;
      })
      .addCase(approveScanRecordAsync.fulfilled, (state) => {
        state.working = null;
      })
      .addCase(approveScanRecordAsync.rejected, (state, action) => {
        state.working = null;
        state.error = action.payload;
      });
  },
});

export const {
  setBatches,
  setRecords,
  setLoadingBatches,
  setLoadingRecords,
  setError,
  clearScanRecords,
} = rebuildScansSlice.actions;

export default rebuildScansSlice.reducer;
